import Layout from "antd/es/layout";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import NewAppHeader from "./NewAppHeader";
import MobileVerification from '../MobileVerification';
import { Modal, Button, Spin } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import hourglassIcon from '../../assets/icons/hourglass.svg';
import lockIcon from '../../assets/icons/lock.svg';
import api from '../../services/api';
import { setPlanExpiresInSeconds } from '../../redux/upgradeSlice';
import NewAppFooter from "./NewAppFooter";
import { MobileNavFooter } from "./MobileNavFooter";
import { Suspense, useEffect, useState, useContext, useRef } from "react";
import { HideLayoutContext } from '../auth/ProtectedRoute';
import ScrollToTop from "../common/scrollToTop";
import ScrollToTopButton from "../common/ScrollToTopButton";
import { useAuth0 } from '@auth0/auth0-react';
import { useRegisterUser } from '../../hooks/useRegisterUser';
import ToastMsg from '../common/ToastMsg';
import './banner.css';
import { gsap } from "gsap";
import { setisModalOpen, setMobileUnverifiedUser, setShowModalRequest, setVerificationStatusLoaded } from "../../redux/mobileVerificationSlice";
import { degreeSearchChange, setSearchedList, setIsCourse, setIsRole, setSearchType } from '../../redux/searchSlice';
import { setUserInfo } from "../../redux/userSlice";
import { useAppSelector } from "../../hooks/useTypedSelector";
const { Content, Footer } = Layout;

// List of non-protected routes that should not be wrapped with AuthInitializer
const NON_PROTECTED_ROUTES = ['/login', '/callback', '/verify-email', '/new-user-verify-email'];

function StudentLayout() {
  const planExpiresInSecondsRedux = useSelector((state: any) => state.upgrade.planExpiresInSeconds);
  const dispatch = useDispatch();
  // Local ticking state for countdown
  const [localPlanExpiresInSeconds, setLocalPlanExpiresInSeconds] = useState<number>(planExpiresInSecondsRedux);
  // Whether the API response included the plan expiry field at all
  const [hasPlanExpiry, setHasPlanExpiry] = useState<boolean>(false);
  const mobileUnverifiedUser = useSelector((state: any) => state.mobileVerificationData.mobileUnverifiedUser);
  const isFreeUser = useAppSelector((state: any) => state.userData.freeUser)
  const currentPlan = useAppSelector((state: any) => state.userData.currentPlan);
  const phoneVerified = useAppSelector((state: any) => state.userData.phoneVerified);
  const verificationStatusLoaded = useSelector((state: any) => state.mobileVerificationData.verificationStatusLoaded);
  const showModalRequest = useSelector((state: any) => state.mobileVerificationData.showModalRequest);
  const [showMobileVerifyModal, setShowMobileVerifyModal] = useState(false);
  const [userPersonalLoaded, setUserPersonalLoaded] = useState(false);
  // Fetch plan expiry info on initial render
  const { isAuthenticated } = useAuth0();
  const fetchPlanExpiryStarted = useRef(false);

  useEffect(() => {
    async function fetchPlanExpiry() {
      if (sessionStorage.getItem('is_logging_out') === 'true') return;
      if (fetchPlanExpiryStarted.current) return;
      fetchPlanExpiryStarted.current = true;

      try {
        const response = await api.get('user-personal');
        if (response.status === 200 && response.data?.data) {
          const dataObj = response.data.data;
          if (dataObj?.phoneVerified === true && dataObj?.phoneVerified !== null && dataObj?.phoneVerified !== undefined) {
            dispatch(setMobileUnverifiedUser(false));
          }
          else {
            dispatch(setMobileUnverifiedUser(true));
          }
          dispatch(setUserInfo(dataObj));
          const planExpirySeconds = dataObj?.planExpireIn;

          if (typeof planExpirySeconds === "number" && !isNaN(planExpirySeconds)) {
            setHasPlanExpiry(true);
            dispatch(setPlanExpiresInSeconds(planExpirySeconds));
          } else {
            setHasPlanExpiry(false);
            dispatch(setPlanExpiresInSeconds(0));
          }
        }
      } catch (err) {
        if (sessionStorage.getItem('is_logging_out') !== 'true') {
          console.error("Failed to fetch plan expiry", err);
          fetchPlanExpiryStarted.current = false; // allow retry on error if needed
        }
      } finally {
        dispatch(setVerificationStatusLoaded(true));
        setUserPersonalLoaded(true);
      }
    }
    let onAppInit: (() => void) | undefined;
    let onRegisterSuccess: (() => void) | undefined;
    if (isAuthenticated) {
      // Always try an initial fetch if authenticated
      fetchPlanExpiry();

      try {
        const registerStatus = sessionStorage.getItem('register_status');
        if (registerStatus !== '201') {
          onRegisterSuccess = () => fetchPlanExpiry();
          window.addEventListener('registerSuccess', onRegisterSuccess);

          const initialized = sessionStorage.getItem('app_initialized');
          if (!initialized) {
            onAppInit = () => fetchPlanExpiry();
            window.addEventListener('appInitialized', onAppInit);
          }
        }
      } catch (e) {
        console.error('Error in authentication init listeners', e);
      }
    }
    return () => {
      if (onAppInit) window.removeEventListener('appInitialized', onAppInit);
      if (onRegisterSuccess) window.removeEventListener('registerSuccess', onRegisterSuccess);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, dispatch]);

  // Keep ticking down every second if > 0
  useEffect(() => {
    setLocalPlanExpiresInSeconds(planExpiresInSecondsRedux);
  }, [planExpiresInSecondsRedux]);

  useEffect(() => {
    if (typeof localPlanExpiresInSeconds !== 'number' || localPlanExpiresInSeconds <= 0) return;
    const interval = setInterval(() => {
      setLocalPlanExpiresInSeconds(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [localPlanExpiresInSeconds]);
  const location = useLocation();
  const isProtectedRoute = !NON_PROTECTED_ROUTES.includes(location.pathname);
  const hideLayout = useContext(HideLayoutContext);
  const navigate = useNavigate();
  const showTrialBanner = localPlanExpiresInSeconds > 0 && localPlanExpiresInSeconds <= 7 * 86400;
  const [bannerVisible, setBannerVisible] = useState(true);
  const [bannerAnimating, setBannerAnimating] = useState(false);


  const bannerRef = useRef<HTMLDivElement | null>(null);
  const mobileBannerRef = useRef<HTMLDivElement | null>(null);
  const hourglassRef = useRef<HTMLImageElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);


  useEffect(() => {
    if (bannerVisible && bannerRef.current) {
      // Slide-in animation for banner
      gsap.fromTo(
        bannerRef.current,
        { y: -80, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }
      );

      if (hourglassRef.current) {
        gsap.to(hourglassRef.current, {
          rotate: 360,
          duration: 3,
          repeat: -1,
          delay: 2,
        });
      }
    }
  }, [bannerVisible, mobileUnverifiedUser, verificationStatusLoaded, userPersonalLoaded, isAuthenticated]);

  useEffect(() => {
    const setBannerTop = () => {
      const header = document.querySelector('.appHeaderContainer') || document.querySelector('.nav-header');
      if (!header) return;
      const headerHeight = (header as HTMLElement).getBoundingClientRect().height;
      const topValue = `${Math.ceil(headerHeight)}px`;

      document.documentElement.style.setProperty('--banner-top-position', topValue);
      document.documentElement.style.setProperty('--mobile-banner-top-position', topValue);

      if (bannerRef.current) {
        bannerRef.current.style.top = topValue;
      }
      if (mobileBannerRef.current) {
        mobileBannerRef.current.style.top = topValue;
      }
    };

    const initialTimers: NodeJS.Timeout[] = [];
    requestAnimationFrame(() => {
      setBannerTop();
      initialTimers.push(
        setTimeout(setBannerTop, 50),
        setTimeout(setBannerTop, 150),
        setTimeout(setBannerTop, 300),
        setTimeout(setBannerTop, 500),
        setTimeout(setBannerTop, 1000)
      );
    });

    // Use MutationObserver to watch for DOM changes
    const observer = new MutationObserver((mutations) => {
      setBannerTop();
    });

    let headerEl = document.querySelector('.appHeaderContainer') || document.querySelector('.nav-header');
    if (headerEl) {
      observer.observe(headerEl, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }

    let resizeObserver: ResizeObserver | undefined;
    const setupResizeObserver = () => {
      headerEl = document.querySelector('.appHeaderContainer') || document.querySelector('.nav-header');
      if (headerEl) {
        resizeObserver = new ResizeObserver(() => {
          setBannerTop();
        });
        resizeObserver.observe(headerEl);
        return true;
      }
      return false;
    };

    setupResizeObserver();

    // Retry ResizeObserver setup if header not found initially
    let retryInterval: NodeJS.Timeout | undefined;
    let retryTimeout: NodeJS.Timeout | undefined;

    if (!headerEl) {
      retryInterval = setInterval(() => {
        if (setupResizeObserver()) {
          if (retryInterval) clearInterval(retryInterval);
          retryInterval = undefined;
        }
      }, 100);
      retryTimeout = setTimeout(() => {
        if (retryInterval) {
          clearInterval(retryInterval);
          retryInterval = undefined;
        }
      }, 2000);
    }

    // Event listeners for various scenarios
    window.addEventListener('orientationchange', setBannerTop);
    window.addEventListener('load', setBannerTop);
    window.addEventListener('resize', setBannerTop);
    document.addEventListener('DOMContentLoaded', setBannerTop);

    // Cleanup
    return () => {
      initialTimers.forEach(clearTimeout);
      observer.disconnect();
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (retryInterval) {
        clearInterval(retryInterval);
      }
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      window.removeEventListener('orientationchange', setBannerTop);
      window.removeEventListener('load', setBannerTop);
      window.removeEventListener('resize', setBannerTop);
      document.removeEventListener('DOMContentLoaded', setBannerTop);
    };
  }, [bannerVisible]);


  // Handle banner close and auto-reappear
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (!bannerVisible && showTrialBanner) {
      timer = setTimeout(() => {
        setBannerAnimating(true);
        setBannerVisible(true);
        setTimeout(() => setBannerAnimating(false), 600); // Animation duration
      }, 5 * 60 * 1000); // 5 minutes
    }
    return () => timer && clearTimeout(timer);
  }, [bannerVisible, showTrialBanner]);
  // Format as hh:mm:ss if less than 1 day, otherwise show days left
  function formatTimeLeft(seconds: number) {
    if (seconds >= 86400) {
      const days = Math.floor(seconds / 86400);
      return `${days} day${days === 1 ? '' : 's'}`;
    } else {
      const hrs = Math.floor(seconds / 3600).toString().padStart(2, '0');
      const mins = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
      const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
      return `${hrs}:${mins}:${secs}`;
    }
  }

  const hide_routes = window.location.pathname === "/compare-college" || window.location.pathname === "/compare-course" || window.location.pathname === "/"
    || window.location.pathname === "/search" || window.location.pathname === "/settings" || window.location.pathname.includes("/error")
    || window.location.pathname === "/session-expired" || window.location.pathname === "/internal-server-error" || window.location.pathname === "/upgrade" || window.location.pathname === "/my-profile" || window.location.pathname === "/mobile-verification"
    || window.location.pathname === "/fastrack" || window.location.pathname === "/talk-to-expert" || window.location.pathname === "/onboard" || window.location.pathname === "/referral";
  const layoutContent = (
    <>
      <ScrollToTop />
      {!hide_routes && isAuthenticated && <ScrollToTopButton />}
      {!hideLayout && <NewAppHeader onOpenMobileVerificationModal={() => {
        sessionStorage.setItem("mobile_verify_redirect", window.location.pathname);
        setShowMobileVerifyModal(true)
        setisModalOpen(true)
      }} />}

      {isAuthenticated && isProtectedRoute && verificationStatusLoaded && userPersonalLoaded && mobileUnverifiedUser && window.location.pathname !== "/mobile-verification" && (
        <div ref={mobileBannerRef} className="mobileUnverifiedBanner">
          <div className="banner-inner">
            <img src={lockIcon} alt="lock" style={{ width: 18, height: 18 }} />
            <div style={{ maxWidth: 840 }}>
              You have access to PARTNER univerisities & limited features only. Verify your mobile for complete access.
            </div>
            <button
              className="verifyNowBtn"
              onClick={() => {
                setShowMobileVerifyModal(true);
                dispatch(setisModalOpen(true));
                try { sessionStorage.setItem('mobile_verify_request', 'true'); } catch (e) { }
              }}
              style={{ cursor: "pointer" }}
            >
              Verify Now
            </button>
          </div>
        </div>
      )}

      {isAuthenticated && isProtectedRoute && verificationStatusLoaded && userPersonalLoaded && isFreeUser && window.location.pathname !== "/mobile-verification" && (
        <div ref={mobileBannerRef} className="mobileUnverifiedBanner">
          <div className="banner-inner">
            <img src={lockIcon} alt="lock" style={{ width: 18, height: 18 }} />
            <div style={{ maxWidth: 840 }}>
              {hasPlanExpiry
                ? 'Your free trial has expired, so you have limited access. Upgrade to unlock all features.'
                : "You have limited access as a guest. Start your free trial to unlock more features."}
            </div>
            <button
              className="verifyNowBtn"
              onClick={() => {
                navigate('/upgrade');
              }}
              style={{ cursor: "pointer" }}
            >
              {hasPlanExpiry ? 'Upgrade Now' : 'Start Trial'}
            </button>
          </div>
        </div>
      )}
      {hasPlanExpiry && (showTrialBanner || localPlanExpiresInSeconds <= 0) && !hideLayout && bannerVisible && !isFreeUser && (
        <div ref={bannerRef} className={`premiumUpgradeBanner${bannerAnimating ? ' slide-down' : ''}`}>
          <div className="banner-content">
            <div className="imgAndText">
              <img className="hourGlassImg" ref={hourglassRef} src={hourglassIcon} alt="hourglass" style={{ width: 24, height: 24 }} />
              <span>
                {localPlanExpiresInSeconds > 0
                  ? `Your free trial has ${formatTimeLeft(localPlanExpiresInSeconds)} left. Upgrade now to keep full access.`
                  : 'Your free trial has expired. Upgrade to unlock all features.'}
              </span>
            </div>
            <Button ref={buttonRef} className="upgradeRedirectBtn" onClick={() => navigate('/upgrade')}>
              Upgrade Now!
            </Button>
          </div>
          <button
            type="button"
            onClick={() => setBannerVisible(false)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setBannerVisible(false); }}
            aria-label="Close notification"
            style={{
              cursor: 'pointer',
              fontSize: 20,
              color: '#333',
              fontWeight: 'bold',
              marginLeft: 16,
              background: 'transparent',
              border: 'none',
              padding: 0,
            }}
          >
            &#10005;
          </button>
        </div>


      )}

      <Content className={location.pathname === "/search" ? "main-container-layout-search" : "main-container-layout"}>
        <Suspense fallback={<div className="page-skeleton"></div>}>
          <Outlet />
        </Suspense>
      </Content>
      {!hideLayout && (
        <Footer className="app-footer-container">
          <NewAppFooter />
        </Footer>
      )}
      {!hideLayout && <MobileNavFooter />}
    </>
  );

  useEffect(() => {
    if (!mobileUnverifiedUser) {
      setShowMobileVerifyModal(false);
    }
  }, [mobileUnverifiedUser]);

  useEffect(() => {
    if (!isAuthenticated) {
      setUserPersonalLoaded(false);
    }
  }, [isAuthenticated]);

  // Open modal if some component requested it via redux flag
  useEffect(() => {
    if (showModalRequest) {
      // only open if user-personal has finished loading to avoid focus/overlay races
      if (verificationStatusLoaded && userPersonalLoaded) {
        if (!mobileUnverifiedUser) {
          dispatch(setShowModalRequest(false));
          return;
        }
        dispatch(setisModalOpen(true));
        try { sessionStorage.setItem("mobile_verify_redirect", window.location.pathname); } catch (e) { }
        setShowMobileVerifyModal(true);
        dispatch(setShowModalRequest(false));
      } else {
        // leave the redux flag set; it will be handled once verification/userPersonal finish
      }
    }
  }, [showModalRequest, dispatch, verificationStatusLoaded, userPersonalLoaded, mobileUnverifiedUser]);

  useEffect(() => {
    try {
      const requested = sessionStorage.getItem('mobile_verify_request');
      if (requested && mobileUnverifiedUser) {
        if (verificationStatusLoaded && userPersonalLoaded) {
          setShowMobileVerifyModal(true);
          dispatch(setisModalOpen(true));
        }
      }
    } catch (e) {
      console.error("Error reading mobile_verify_request from sessionStorage:", e);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileUnverifiedUser]);

  useEffect(() => {
    if (verificationStatusLoaded && userPersonalLoaded && isAuthenticated) {
      const isGuestUnverified = currentPlan === 'guest-user' && phoneVerified === false;
      const isUnverified = mobileUnverifiedUser === true;
      const isFree = isFreeUser === true;

      if (location.pathname === '/' && (isGuestUnverified || isUnverified)) {
        console.log('Restricted user (Free/Unverified/Guest) detected on home page - redirecting to /search');
        navigate('/search', { replace: true });
      }

      if (location.pathname === '/' && isFree) {
        console.log('Free user detected on home page - redirecting to /upgrade');
        navigate('/upgrade', { replace: true });
      }
    }
  }, [location.pathname, currentPlan, phoneVerified, mobileUnverifiedUser, isFreeUser, verificationStatusLoaded, userPersonalLoaded, isAuthenticated, navigate]);

  // Auto-redirect free users when navigating to restricted slugs
  useEffect(() => {
    if (!isFreeUser || !isAuthenticated) return;
    const pathname = location.pathname.toLowerCase();
    const restrictedSlugs = [
      '/recommend-',
      '/mylist-',
      '/compare-course',
      '/talk-to-expert',
      '/referral',
      '/fasttrack',
      '/role'
    ];

    const isRestricted = restrictedSlugs.some(slug => pathname.startsWith(slug));

    if (isRestricted) {
      console.log('Free user attempted to access restricted path:', pathname);
      navigate('/upgrade', { replace: true });
      ToastMsg('warning', 'Please upgrade to access this feature.');
    }
  }, [isFreeUser, location.pathname, navigate, isAuthenticated]);

  // Auto-open mobile verification modal when user navigates to certain protected slugs
  useEffect(() => {
    if (!mobileUnverifiedUser) return;
    const pathname = location.pathname.toLowerCase();
    const triggerSlugs = [
      'recommend',
      'mylist',
      'compare-course',
      'similar-colleges',
      'talk-to-expert',
      'referral'
    ];
    const shouldOpen = triggerSlugs.some(s => pathname.includes(s));
    if (shouldOpen) {
      sessionStorage.setItem("mobile_verify_redirect", window.location.pathname);
      setShowMobileVerifyModal(true);
      dispatch(setisModalOpen(true));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileUnverifiedUser, location.pathname]);

  const mobileVerifyModal = (mobileUnverifiedUser && isAuthenticated && verificationStatusLoaded && userPersonalLoaded) ? (
    <Modal
      open={showMobileVerifyModal}
      footer={null}
      onCancel={() => {
        setShowMobileVerifyModal(false);
        const pathname = location.pathname.toLowerCase();
        const triggerSlugs = [
          'recommend',
          'mylist',
          'shortlist',
          'compare-college',
          'compare-course',
          'similar-colleges'
        ];
        const shouldRedirect = triggerSlugs.some(s => pathname.includes(s));
        if (shouldRedirect) {
          console.debug('MobileVerify modal closed. pathname:', pathname);
          let sessionRequested = false;
          try {
            sessionRequested = sessionStorage.getItem('mobile_verify_request') === 'true';
          } catch (e) {
            console.debug('Error reading sessionStorage.mobile_verify_request', e);
          }

          if (pathname.includes('compare-course') || sessionRequested) {
            console.debug('Redirecting to /compare-college');
            navigate('/compare-college');
          } else {
            console.debug('Redirecting to /college');
            navigate('/college');
          }
        }
        // If modal closed while on search page, clear search typing and results
        if (pathname === '/search' || pathname.includes('/search')) {
          dispatch(degreeSearchChange(''));
          dispatch(setSearchedList([]));
          // switch the UI segment back to College
          dispatch(setSearchType('College'));
          dispatch(setIsCourse(false));
          dispatch(setIsRole(false));
        }
        try { sessionStorage.removeItem('mobile_verify_request'); } catch (e) { /* ignore */ }
      }}
      centered
      width={'80%'}
      destroyOnClose
      wrapClassName="mobile-verify-modal-wrapper"
      maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}
      bodyStyle={{ background: 'transparent', padding: 0 }}
    >
      <MobileVerification isModal={true} onVerified={() => setShowMobileVerifyModal(false)} />
    </Modal>
  ) : null;

  return (
    <Layout className="app-layout">
      {isProtectedRoute ? (
        <AuthInitializer
          verificationStatusLoaded={verificationStatusLoaded}
          userPersonalLoaded={userPersonalLoaded}
        >
          {layoutContent}
        </AuthInitializer>
      ) : (
        layoutContent
      )}
      {mobileVerifyModal}
    </Layout>
  );
}

// New component to handle initial registration
const AuthInitializer = ({
  children,
  verificationStatusLoaded,
  userPersonalLoaded
}: {
  children: React.ReactNode,
  verificationStatusLoaded: boolean,
  userPersonalLoaded: boolean
}) => {
  const { isAuthenticated, user, isLoading } = useAuth0();
  const { registerUser } = useRegisterUser();
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasRegisterError, setHasRegisterError] = useState(false);
  const [regResponse, setRegResponse] = useState<any>(null);
  const [showReload, setShowReload] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const didStartRegistration = useRef(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isInitialized && !hasRegisterError) {
        setShowReload(true);
      }
    }, 20000); // 20 seconds fallback

    return () => clearTimeout(timeout);
  }, [isInitialized, hasRegisterError]);

  useEffect(() => {
    const initializeAuth = async () => {
      if (sessionStorage.getItem('register_failed_once')) return;
      if (isAuthenticated && user && !isInitialized && !hasRegisterError) {

        // 1. Perform registration if not already done
        let currentResponse = regResponse;
        if (!didStartRegistration.current && !regResponse) {
          didStartRegistration.current = true;
          try {
            const storedReferral = sessionStorage.getItem('referral_code') || '';
            const storedCounsellor = sessionStorage.getItem('counsellor_code') || '';
            const registerUserData = {
              referralCode: storedReferral || '',
              counsellorCode: storedCounsellor || ''
            };

            const response = await registerUser(registerUserData);
            console.log('Initial registration response:', response);
            setRegResponse(response);
            currentResponse = response;

            try { sessionStorage.removeItem('referral_code'); } catch (e) { console.error("Error", e) }
            try { sessionStorage.removeItem('counsellor_code'); } catch (e) { console.error("Error", e) }
          } catch (error: any) {
            console.error('Failed to register user:', error);
            if (error?.isSessionExpired || sessionStorage.getItem('session_expired')) return;
            ToastMsg('error', error.message || 'Failed to initialize user session');
            setHasRegisterError(true);
            sessionStorage.setItem('register_failed_once', 'true');
            navigate('/internal-server-error', { state: { messageText: error.message || 'Internal server error' } });
            return;
          }
        }

        if (!currentResponse || !verificationStatusLoaded || !userPersonalLoaded) {
          console.log('AuthInitializer: Still waiting for dependencies...', {
            hasResponse: !!currentResponse,
            verificationStatusLoaded,
            userPersonalLoaded
          });
          return;
        }

        // 3. Finalize initialization and handle redirects
        try {
          const currentPath = (location && location.pathname) ? location.pathname.toLowerCase() : (window.location.pathname || '/');
          console.log('AuthInitializer: Finalizing initialization', { currentPath });

          let targetPath = '';

          if (currentResponse.message === "New User Signed Up" && currentResponse.user?.emailVerified === true) {
            targetPath = "/mobile-verification";
          } else if (currentResponse.message === "New User Signed Up") {
            targetPath = '/new-user-verify-email';
          } else if (currentResponse.message === "Email Not Verified") {
            targetPath = '/verify-email';
          } else if (!user.email_verified) {
            targetPath = '/verify-email';
          }

          if (targetPath) {
            if (currentPath !== targetPath.toLowerCase()) {
              console.log('AuthInitializer: Redirecting to auth status path', targetPath);
              navigate(targetPath, { replace: true });
              return;
            }
          }

          try {
            sessionStorage.setItem('app_initialized', 'true');
            window.dispatchEvent(new Event('appInitialized'));
          } catch (e) { }

          const isMobileVerified = currentResponse?.user?.phoneVerified === true;
          const userPlan = currentResponse?.user?.currentPlan;
          const isGuestUnverified = userPlan === 'guest-user' && !isMobileVerified;
          const isFree = currentResponse?.user?.freeUser === true;

          if (!isMobileVerified || isGuestUnverified || isFree) {
            const allowedPages = ['/college', '/course', '/compare-college', '/upgrade', '/my-profile', '/settings', '/search', '/onboard', '/recommend', '/mylist'];
            const shouldAllow = allowedPages.some(page => currentPath.includes(page)) && currentPath !== '/';

            if (!shouldAllow) {
              const redirectTarget = (isFree && currentPath === '/') ? '/upgrade' : '/search';
              if (currentPath !== redirectTarget) {
                console.log('AuthInitializer: Redirecting restricted user', redirectTarget);
                navigate(redirectTarget, { replace: true });
                return;
              }
            }
          }

          const appStateJSON = sessionStorage.getItem('auth_redirect_in_progress');
          if (appStateJSON) {
            try {
              const appState = JSON.parse(appStateJSON);
              const returnTo = appState?.returnTo || '/';
              sessionStorage.removeItem('auth_redirect_in_progress');
              if (currentPath !== returnTo.toLowerCase() && returnTo !== '/') {
                console.log('AuthInitializer: Found pending redirect', returnTo);
                navigate(returnTo);
                return;
              }
            } catch (e) { }
          }

          console.log('AuthInitializer: Initial registration fully completed');
          setIsInitialized(true);
        } catch (error) {
          console.error('Error during final initialization:', error);
        }
      }
    };

    if (!isLoading) {
      initializeAuth();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, isLoading, registerUser, isInitialized, hasRegisterError, navigate, verificationStatusLoaded, userPersonalLoaded, regResponse, location.pathname]);


  // Show loading state while initializing
  if (isAuthenticated && (!isInitialized || !verificationStatusLoaded || !userPersonalLoaded) && !hasRegisterError) {

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#fff',
        fontFamily: "'Poppins', sans-serif"
      }}>
        <div style={{ textAlign: 'center' }}>
          <Spin size="large" />
          <h2 style={{ marginTop: '20px', color: '#1a3353', fontWeight: 500 }}>Loading...</h2>

          {showReload && (
            <div style={{ marginTop: '30px', animation: 'fadeIn 0.5s ease-out' }}>
              <p style={{ color: '#8898aa', fontSize: '13px', marginBottom: '15px' }}>Taking longer than usual?</p>
              <Button
                type="primary"
                onClick={() => window.location.reload()}
                style={{
                  borderRadius: '8px',
                  height: '40px',
                  padding: '0 24px',
                  background: '#0056D2',
                  border: 'none',
                  fontWeight: 500
                }}
              >
                Reload Page
              </Button>
            </div>
          )}
        </div>
        <style>{`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  return <>{children}</>;
};

export default StudentLayout;
